import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Progress } from '../components/ui/progress';
import { Trophy, Calendar, Target, CheckCircle, Play } from 'lucide-react';

const Challenges = () => {
  const { token } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [myChallenges, setMyChallenges] = useState([]);
  const [loading, setLoading] = useState({});

  useEffect(() => {
    fetchChallenges();
    fetchMyChallenges();
  }, []);

  const fetchChallenges = async () => {
    try {
      const response = await axios.get(`${API}/challenges`);
      setChallenges(response.data.challenges);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchMyChallenges = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/challenges/my`, { headers });
      setMyChallenges(response.data.challenges);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleJoin = async (challengeId) => {
    setLoading({ ...loading, [challengeId]: true });
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/challenges/join`, { challenge_id: challengeId }, { headers });
      toast.success('Défi rejoint !');
      fetchMyChallenges();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Échec de l\'inscription');
    } finally {
      setLoading({ ...loading, [challengeId]: false });
    }
  };

  const handleProgress = async (joinId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/challenges/${joinId}/progress`, {}, { headers });
      toast.success(`Progression mise à jour : ${response.data.progress}%`);
      fetchMyChallenges();
    } catch (error) {
      toast.error('Échec de la mise à jour');
    }
  };

  const isJoined = (challengeId) => myChallenges.some(c => c.challenge_id === challengeId && c.status === 'active');

  return (
    <DashboardLayout title="Espace Défis">
      <div className="max-w-6xl mx-auto">
        {/* My Active Challenges */}
        {myChallenges.filter(c => c.status === 'active').length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-12">
            <h2 className="font-serif text-2xl text-white mb-6">Tes défis en cours</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {myChallenges.filter(c => c.status === 'active').map((challenge) => (
                <div key={challenge.join_id} className="card-premium p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-serif text-xl text-white">{challenge.challenge_name}</h3>
                      <p className="text-[#A1A1AA] text-sm mt-1">
                        Fin : {new Date(challenge.end_date).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <span className="text-[#D4AF37] font-serif text-2xl">{challenge.progress}%</span>
                  </div>
                  <Progress value={challenge.progress} className="h-2 bg-[#1C1C1C] mb-4" />
                  <Button onClick={() => handleProgress(challenge.join_id)} className="btn-outline-gold px-4 py-2 text-sm" data-testid={`progress-${challenge.join_id}`}>
                    <CheckCircle size={16} className="mr-2" />
                    Enregistrer ma progression
                  </Button>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Available Challenges */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <h2 className="font-serif text-2xl text-white mb-6">Défis disponibles</h2>
          {challenges.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-6">
              {challenges.map((challenge) => {
                const joined = isJoined(challenge.challenge_id);
                return (
                  <div key={challenge.challenge_id} className="card-premium overflow-hidden">
                    <div className="aspect-video relative">
                      <img src={challenge.image} alt={challenge.name} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4">
                        <span className="text-[#E5C585] text-xs font-accent tracking-wider flex items-center gap-2">
                          <Calendar size={12} />
                          {challenge.duration_days} JOURS
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="font-serif text-xl text-white mb-2">{challenge.name}</h3>
                      <p className="text-[#A1A1AA] text-sm mb-4">{challenge.description}</p>
                      <Button
                        onClick={() => handleJoin(challenge.challenge_id)}
                        disabled={joined || loading[challenge.challenge_id]}
                        className={joined ? 'btn-outline-gold opacity-50 cursor-not-allowed w-full py-3' : 'btn-gold w-full py-3'}
                        data-testid={`join-${challenge.challenge_id}`}
                      >
                        {joined ? 'Déjà inscrite' : loading[challenge.challenge_id] ? 'Inscription...' : (
                          <><Play size={16} className="mr-2" />Rejoindre le défi</>
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="card-premium p-12 text-center">
              <Trophy className="mx-auto text-[#D4AF37]/50 mb-4" size={48} />
              <p className="text-[#71717A] font-light">Aucun défi disponible pour le moment.</p>
              <p className="text-[#52525B] text-sm mt-2">De nouveaux défis arrivent bientôt !</p>
            </div>
          )}
        </motion.div>

        {/* Completed Challenges */}
        {myChallenges.filter(c => c.status === 'completed').length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mt-12">
            <h2 className="font-serif text-2xl text-white mb-6">Défis complétés</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {myChallenges.filter(c => c.status === 'completed').map((challenge) => (
                <div key={challenge.join_id} className="card-premium p-4 flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#D4AF37]/20 flex items-center justify-center">
                    <Trophy className="text-[#D4AF37]" size={24} />
                  </div>
                  <div>
                    <h4 className="text-white font-serif">{challenge.challenge_name}</h4>
                    <p className="text-[#52525B] text-xs">Complété</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Challenges;
